// =============================================================================
// LadderRegisterTab — the ladder register and its inspections (mig 430,
// 8 Oct 2026)
// =============================================================================
// James, 8 Oct 2026: "I also think we need a ladder register with inspections
// done on them for Ventia as it's in the SWMS I think."
//
// Everyone sees the ladders (through v_ladders) and anyone can log an
// inspection — the crew are the ones holding the ladder. Managers add and
// edit ladders. A fail must say what was wrong and the database takes the
// ladder out of use; a manager puts it back once it is repaired and has
// passed again. Inspections are a record: there is no edit and no delete.
//
// Mig 432 — "Kept at" is a fleet vehicle, the depot, or typed (KeptAtField).
// =============================================================================

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, X, ClipboardCheck, ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Pill, Button, Spinner, EmptyState, Field, Input } from "@/components/ui";
import { fmtDate, daysFromNow } from "@/lib/dates";
import KeptAtField, { type KeptAt } from "./KeptAtField";

interface Ladder {
  asset_id: string; asset_code: string | null; description: string; make_model: string | null;
  serial_number: string | null; location: string | null; is_active: boolean;
  next_inspection_due: string | null; last_inspection_result: string | null;
  last_inspection_date: string | null; notes: string | null;
  kept_in_vehicle_id: string | null; kept_at: string | null;
}
interface Inspection {
  inspection_id: string; inspection_date: string; inspector_name: string | null; result: "pass" | "fail";
  defects_found: string | null; action_taken: string | null; next_inspection_due: string | null;
}

// What gets looked at. Kept short so it is actually done.
const CHECKS: { key: string; label: string }[] = [
  { key: "stiles",    label: "Stiles straight, no cracks, splits or dents" },
  { key: "rungs",     label: "Rungs / treads tight, none bent, missing or worn" },
  { key: "feet",      label: "Feet fitted, non-slip and not worn through" },
  { key: "locks",     label: "Spreaders, locks and hinges work and hold" },
  { key: "ropes",     label: "Extension rope, pulley and clutches sound (or not fitted)" },
  { key: "label",     label: "Load rating label readable — industrial rated" },
  { key: "clean",     label: "Clean — no paint, oil or mud hiding damage" },
];

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function LadderRegisterTab({ search, canEdit, user }: {
  search: string; canEdit: boolean;
  user: { user_id?: string | null; employee_id?: string | null; first_name?: string | null; last_name?: string | null } | null;
}) {
  const qc = useQueryClient();
  const [inspecting, setInspecting] = useState<Ladder | null>(null);
  const [editing, setEditing] = useState<Ladder | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const { data: ladders = [], isLoading, error } = useQuery({
    queryKey: ["assets", "ladders"],
    queryFn: async (): Promise<Ladder[]> => {
      const { data, error } = await (supabase as any).from("v_ladders").select("*").order("asset_code");
      if (error) throw error;
      return (data ?? []) as Ladder[];
    },
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["assets", "ladders"] }); qc.invalidateQueries({ queryKey: ["ladder_inspections"] }); };

  async function backInUse(l: Ladder) {
    const { error } = await (supabase as any).from("company_assets")
      .update({ is_active: true, updated_by: user?.user_id ?? null }).eq("asset_id", l.asset_id);
    if (error) { alert(error.message); return; }
    refresh();
  }

  if (isLoading) return <div className="py-10 flex justify-center"><Spinner /></div>;
  if (error) return <Card><div className="p-4 text-[13px] text-danger">Could not load the ladders: {(error as any)?.message ?? "unknown error"}</div></Card>;

  const q = search.trim().toLowerCase();
  const shown = ladders.filter(l => !q || [l.asset_code, l.description, l.make_model, l.serial_number, l.kept_at].some(x => (x ?? "").toLowerCase().includes(q)));

  return (
    <>
      {canEdit && <div className="mb-3"><Button icon={Plus} onClick={() => setEditing("new")}>Add ladder</Button></div>}
      {shown.length === 0 ? (
        <Card><div className="p-6"><EmptyState title="No ladders on the register yet" description={canEdit ? "Add each ladder once, then log its inspections here." : "Ask the office to add the ladders."} /></div></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="border-b border-line">
                {["", "Code", "Ladder", "Kept at", "Last inspected", "Next due", "State", ""].map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {shown.map((l, i) => {
                  const days = l.next_inspection_due ? daysFromNow(l.next_inspection_due) : null;
                  const overdue = days != null && days < 0;
                  const isOpen = open === l.asset_id;
                  return [
                    <tr key={l.asset_id} className={`${i % 2 ? "bg-ink/40" : ""} border-b border-line`}>
                      <td className="pl-3">
                        <button type="button" onClick={() => setOpen(isOpen ? null : l.asset_id)} className="text-text-muted hover:text-text" title="Inspection history">
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-mono text-accent text-[12px]">
                        {canEdit ? <button type="button" className="hover:underline" onClick={() => setEditing(l)}>{l.asset_code}</button> : l.asset_code}
                      </td>
                      <td className="px-4 py-3">{l.description}{l.make_model ? <span className="text-text-muted"> · {l.make_model}</span> : null}</td>
                      <td className="px-4 py-3 text-text-muted">{l.kept_at || "—"}</td>
                      <td className="px-4 py-3 font-mono text-[12px] text-text-muted">{l.last_inspection_date ? fmtDate(l.last_inspection_date) : "never"}</td>
                      <td className="px-4 py-3 font-mono text-[12px]">
                        {l.next_inspection_due
                          ? <span className={overdue ? "text-danger" : days != null && days <= 14 ? "text-warn" : "text-text-muted"}>{fmtDate(l.next_inspection_due)}{overdue ? " · overdue" : ""}</span>
                          : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {!l.is_active
                          ? <Pill small tone="expired" label={l.last_inspection_result === "fail" ? "Failed — out of use" : "Out of use"} />
                          : !l.last_inspection_date ? <Pill small tone="warning" label="Not inspected" />
                          : overdue ? <Pill small tone="expired" label="Overdue" />
                          : <Pill small tone="approved" label="In use" />}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canEdit && !l.is_active && l.last_inspection_result === "pass" && (
                          <button type="button" className="text-accent text-[12px] hover:underline mr-3" onClick={() => void backInUse(l)}>Back in use</button>
                        )}
                        <Button size="sm" variant="secondary" icon={ClipboardCheck} onClick={() => setInspecting(l)}>Inspect</Button>
                      </td>
                    </tr>,
                    isOpen ? <tr key={l.asset_id + "-h"}><td colSpan={8} className="px-4 py-3 bg-ink/30 border-b border-line"><History assetId={l.asset_id} /></td></tr> : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {inspecting && <InspectDialog ladder={inspecting} user={user} onClose={(saved) => { setInspecting(null); if (saved) refresh(); }} />}
      {editing && <LadderDialog ladder={editing === "new" ? null : editing} userId={user?.user_id ?? null} onClose={(saved) => { setEditing(null); if (saved) refresh(); }} />}
    </>
  );
}

function History({ assetId }: { assetId: string }) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["ladder_inspections", assetId],
    queryFn: async (): Promise<Inspection[]> => {
      const { data, error } = await (supabase as any).from("ladder_inspections")
        .select("inspection_id, inspection_date, inspector_name, result, defects_found, action_taken, next_inspection_due")
        .eq("asset_id", assetId).order("inspection_date", { ascending: false }).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Inspection[];
    },
  });
  if (isLoading) return <Spinner />;
  if (rows.length === 0) return <div className="text-[12px] text-text-muted">No inspections logged yet.</div>;
  return (
    <div className="space-y-1.5">
      {rows.map(r => (
        <div key={r.inspection_id} className="text-[12px] flex gap-3 flex-wrap">
          <span className="font-mono text-text-muted">{fmtDate(r.inspection_date)}</span>
          <span className={r.result === "pass" ? "text-ok" : "text-danger"}>{r.result === "pass" ? "Pass" : "Fail"}</span>
          <span className="text-text-muted">{r.inspector_name || "—"}</span>
          {r.defects_found && <span className="text-text">{r.defects_found}</span>}
          {r.action_taken && <span className="text-text-muted">→ {r.action_taken}</span>}
        </div>
      ))}
    </div>
  );
}

function InspectDialog({ ladder, user, onClose }: { ladder: Ladder; user: any; onClose: (saved: boolean) => void }) {
  const [date, setDate] = useState(localToday());
  const [bad, setBad] = useState<Record<string, boolean>>({});
  const [defects, setDefects] = useState("");
  const [action, setAction] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const anyBad = Object.values(bad).some(Boolean);

  async function save() {
    if (anyBad && !defects.trim()) { setErr("Say what is wrong with it."); return; }
    setSaving(true); setErr(null);
    const checks: Record<string, string> = {};
    for (const c of CHECKS) checks[c.key] = bad[c.key] ? "defect" : "ok";
    const { error } = await (supabase as any).from("ladder_inspections").insert({
      asset_id: ladder.asset_id, inspection_date: date,
      inspector_employee_id: user?.employee_id ?? null,
      inspector_name: `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || null,
      result: anyBad ? "fail" : "pass", checks,
      defects_found: defects.trim() || null, action_taken: action.trim() || null,
      created_by: user?.user_id ?? null,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onClose(true);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-12 px-4 overflow-y-auto" onClick={() => onClose(false)}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-lg shadow-xl mb-10" onClick={e => e.stopPropagation()}>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-semibold text-text">Inspect {ladder.asset_code} — {ladder.description}</div>
            <button type="button" onClick={() => onClose(false)} className="text-text-muted hover:text-text"><X size={16} /></button>
          </div>
          <Field label="Date inspected"><Input type="date" value={date} max={localToday()} onChange={(e: any) => setDate(e.target.value)} /></Field>
          <div>
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5">Everything is OK unless you tick it as a defect</div>
            <div className="space-y-1.5">
              {CHECKS.map(c => (
                <label key={c.key} className={`flex items-center gap-2.5 text-[13px] rounded-md border px-3 py-2 ${bad[c.key] ? "border-danger/50 bg-danger/10 text-text" : "border-line text-text"}`}>
                  <input type="checkbox" className="accent-accent" checked={!!bad[c.key]} onChange={e => setBad({ ...bad, [c.key]: e.target.checked })} />
                  <span className="flex-1">{c.label}</span>
                  <span className={`text-[11px] font-mono ${bad[c.key] ? "text-danger" : "text-ok"}`}>{bad[c.key] ? "DEFECT" : "OK"}</span>
                </label>
              ))}
            </div>
          </div>
          {anyBad && (
            <div className="text-[12px] text-danger">This will be logged as a FAIL and the ladder is taken out of use. Tag it out so nobody climbs it.</div>
          )}
          <Field label={anyBad ? "What is wrong" : "Anything to note (optional)"}><Input value={defects} onChange={(e: any) => setDefects(e.target.value)} /></Field>
          {anyBad && <Field label="What you did with it"><Input value={action} onChange={(e: any) => setAction(e.target.value)} placeholder="e.g. Tagged out, back to depot" /></Field>}
          {!anyBad && <div className="text-[12px] text-text-muted">A pass sets the next inspection three months from the date above.</div>}
          {err && <div className="text-[12px] text-danger">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
            <Button variant={anyBad ? "danger" : "primary"} onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : anyBad ? "Log as failed" : "Log as passed"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LadderDialog({ ladder, userId, onClose }: { ladder: Ladder | null; userId: string | null; onClose: (saved: boolean) => void }) {
  const [description, setDescription] = useState(ladder?.description ?? "");
  const [makeModel, setMakeModel] = useState(ladder?.make_model ?? "");
  const [serial, setSerial] = useState(ladder?.serial_number ?? "");
  const [kept, setKept] = useState<KeptAt>({ vehicleId: ladder?.kept_in_vehicle_id ?? null, location: ladder?.location ?? null });
  const [notes, setNotes] = useState(ladder?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!description.trim()) { setErr("Say what the ladder is — type and length."); return; }
    setSaving(true); setErr(null);
    const payload = {
      description: description.trim(), make_model: makeModel.trim() || null, serial_number: serial.trim() || null,
      kept_in_vehicle_id: kept.vehicleId, location: kept.vehicleId ? null : ((kept.location ?? "").trim() || null),
      notes: notes.trim() || null, updated_by: userId,
    };
    const q = (supabase as any).from("company_assets");
    const { error } = ladder
      ? await q.update(payload).eq("asset_id", ladder.asset_id)
      : await q.insert({ ...payload, category: "ladder", created_by: userId, insurance_required: false, insurance_status: "not_required" });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onClose(true);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={() => onClose(false)}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-lg shadow-xl mb-10" onClick={e => e.stopPropagation()}>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[15px] font-semibold text-text">{ladder ? `${ladder.asset_code} — edit` : "Add a ladder"}</div>
            <button type="button" onClick={() => onClose(false)} className="text-text-muted hover:text-text"><X size={16} /></button>
          </div>
          <Field label="What it is"><Input value={description} onChange={(e: any) => setDescription(e.target.value)} placeholder="e.g. 1.8 m fibreglass step ladder" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Make / model"><Input value={makeModel} onChange={(e: any) => setMakeModel(e.target.value)} /></Field>
            <Field label="Serial / tag number"><Input mono value={serial} onChange={(e: any) => setSerial(e.target.value)} /></Field>
          </div>
          <Field label="Kept at"><KeptAtField value={kept} onChange={setKept} /></Field>
          <Field label="Notes"><Input value={notes} onChange={(e: any) => setNotes(e.target.value)} placeholder="optional" /></Field>
          {err && <div className="text-[12px] text-danger">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={() => void save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
